import { useMemo, useState } from 'react'
import { CalendarCheck2, Video } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import { crm2Api, useCrm2Slots, useCrm2Write, type BookingRow } from './api'
import { Field, fieldClass } from './shared'

const DAY = 86_400_000
const dayKey = (iso: string) => new Date(iso).toDateString()
export const timeOf = (iso: string) => new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
export const dayOf = (iso: string) => new Date(iso).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })
/** The viewer's own zone, named, so a time is never ambiguous. */
export const myZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone

/**
 * Pick a time and book the demo: the team's open times for the next two weeks, grouped by day, in
 * the viewer's time. Round robin picks the host unless someone is chosen. "Another time" books
 * outside the team's hours, still only when the host's calendar is clear.
 */
export function BookDemo({ contactId, contactName, hasEmail, onBooked, initialStart }: { contactId: string; contactName: string; hasEmail: boolean; onBooked?: (b: BookingRow) => void; initialStart?: string }) {
  const [from] = useState(() => new Date(Math.floor(Date.now() / 60_000) * 60_000).toISOString())
  const to = useMemo(() => new Date(Date.parse(from) + 14 * DAY).toISOString(), [from])
  const slots = useCrm2Slots(from, to)
  const [day, setDay] = useState<string | null>(initialStart ? dayKey(initialStart) : null)
  const [start, setStart] = useState<string | null>(initialStart ?? null)
  const [host, setHost] = useState<string>('')
  const [anyTime, setAnyTime] = useState(false)
  const [custom, setCustom] = useState('')
  const [notes, setNotes] = useState('')
  const [notify, setNotify] = useState(true)
  const book = useCrm2Write(crm2Api.book, (b) => `Demo booked with ${b.hostName ?? 'the team'}, ${dayOf(b.start)} ${timeOf(b.start)}`)

  const data = slots.data
  const days = useMemo(() => {
    const m = new Map<string, Array<{ start: string; hostIds: string[] }>>()
    for (const s of data?.slots ?? []) m.set(dayKey(s.start), [...(m.get(dayKey(s.start)) ?? []), s])
    return [...m]
  }, [data])
  const bookable = (data?.hosts ?? []).filter((h) => h.bookable)
  const shownDay = day ?? days[0]?.[0] ?? null
  const picked = anyTime ? (custom ? new Date(custom).toISOString() : null) : start
  const pickedSlot = data?.slots.find((s) => s.start === start)
  const hostOk = anyTime || !host || Boolean(pickedSlot?.hostIds.includes(host))

  if (slots.isPending) return <p className="text-sm text-muted-foreground">Reading the team’s calendars…</p>
  if (slots.error) return <p className="text-sm text-danger">Couldn’t read the team’s calendars. Try again in a moment.</p>
  if (!bookable.length)
    return (
      <p className="rounded-lg bg-warning-soft px-3 py-2 text-sm text-warning">
        Nobody on the demo team can be booked yet. In TapCRMv2 Settings → Demo booking, add people, and each of them connects Google Calendar (profile menu) with booking allowed.
      </p>
    )

  const submit = () => {
    if (!picked) return
    book.mutate(
      { contactId, start: picked, hostId: host || null, anyTime, notes: notes.trim() || null, notify },
      { onSuccess: (b) => onBooked?.(b) },
    )
  }

  return (
    <div className="grid gap-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-lg border border-border bg-surface p-0.5" role="group" aria-label="How to pick">
          <button type="button" aria-pressed={!anyTime} onClick={() => setAnyTime(false)} className={cn('rounded-md px-3 py-1', !anyTime ? 'bg-accent font-medium' : 'text-muted-foreground')}>
            Open times
          </button>
          <button type="button" aria-pressed={anyTime} onClick={() => setAnyTime(true)} className={cn('rounded-md px-3 py-1', anyTime ? 'bg-accent font-medium' : 'text-muted-foreground')}>
            Another time
          </button>
        </div>
        <span className="text-xs text-text-muted">
          {data?.durationMin} min · times in your zone ({myZone()})
        </span>
      </div>

      {anyTime ? (
        <Field label="Date and time" hint="Outside the team’s hours is fine; the host’s calendar still has to be clear.">
          <input type="datetime-local" className={fieldClass} value={custom} onChange={(e) => setCustom(e.target.value)} />
        </Field>
      ) : days.length ? (
        <div className="grid gap-2">
          <div className="flex gap-1 overflow-x-auto pb-1" role="tablist" aria-label="Day">
            {days.map(([k, list]) => (
              <button key={k} type="button" role="tab" aria-selected={shownDay === k} onClick={() => setDay(k)} className={cn('shrink-0 rounded-lg border px-3 py-1.5 text-left', shownDay === k ? 'border-brand bg-accent' : 'border-border bg-background hover:border-brand')}>
                <span className="block text-xs font-medium">{dayOf(list[0]!.start)}</span>
                <span className="block text-tiny text-text-muted">{list.length} open</span>
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {(days.find(([k]) => k === shownDay)?.[1] ?? []).map((s) => (
              <button key={s.start} type="button" aria-pressed={start === s.start} onClick={() => setStart(s.start)} className={cn('rounded-md border px-2.5 py-1 text-sm', start === s.start ? 'border-brand bg-primary text-primary-foreground' : 'border-border bg-background hover:border-brand')}>
                {timeOf(s.start)}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <p className="text-muted-foreground">No open times in the next two weeks. Use “Another time”, or widen the hours in Settings.</p>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Host">
          <select className={fieldClass} value={host} onChange={(e) => setHost(e.target.value)} aria-label="Host">
            <option value="">Round robin (whoever is free and due)</option>
            {bookable.map((h) => (
              <option key={h.id} value={h.id} disabled={!anyTime && Boolean(pickedSlot) && !pickedSlot!.hostIds.includes(h.id)}>
                {h.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Invitation">
          <label className="flex h-9 items-center gap-2">
            <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} />
            {hasEmail ? `Email ${contactName} the invitation` : 'No email on file: add one to send an invitation'}
          </label>
        </Field>
      </div>
      <Field label="Notes for the host (optional)">
        <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={2000} placeholder="What they want to see, who else is joining" />
      </Field>
      <div className="flex items-center gap-3">
        <Button onClick={submit} disabled={!picked || !hostOk || book.isPending}>
          <CalendarCheck2 className="size-4" />
          {book.isPending ? 'Booking…' : picked ? `Book ${dayOf(picked)}, ${timeOf(picked)}` : 'Pick a time'}
        </Button>
        <span className="flex items-center gap-1 text-xs text-text-muted">
          <Video className="size-3.5" /> Adds a Google Meet link and moves the deal to Demo · Booked
        </span>
      </div>
    </div>
  )
}
